import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TranslationCache {
  [key: string]: string;
}

const CACHE_KEY = 'hotel_translations_cache';
const CACHE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// Load cache from localStorage
const loadCache = (): TranslationCache => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      // Check if cache is expired
      if (Date.now() - timestamp < CACHE_EXPIRY) {
        return data;
      }
    }
  } catch (error) {
    console.error('Error loading translation cache:', error);
  }
  return {};
};

// Save cache to localStorage
const saveCache = (cache: TranslationCache) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data: cache,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error('Error saving translation cache:', error);
  }
};

// Create a cache key from text + languages
const getCacheKey = (text: string, sourceLang: string, targetLang: string): string => {
  // Use first 100 chars as key to avoid huge keys
  const snippet = text.substring(0, 100);
  return `${sourceLang}__${targetLang}__${snippet}`;
};

// Post-process translation to fix common mistranslations
const fixCommonMistranslations = (text: string): string => {
  const fixes: Record<string, string> = {
    'Restauración': 'Restaurante',
    'restauración': 'restaurante',
  };
  
  let fixedText = text;
  Object.entries(fixes).forEach(([wrong, correct]) => {
    fixedText = fixedText.replace(new RegExp(wrong, 'g'), correct);
  });
  
  return fixedText;
};

export const useTranslation = (text: string | undefined, sourceLang = 'auto', targetLang = 'es') => {
  const [translatedText, setTranslatedText] = useState<string>('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!text) {
      setTranslatedText('');
      return;
    }

    const translateText = async () => {
      const cacheKey = getCacheKey(text, sourceLang, targetLang);
      const cache = loadCache();

      // Check if translation exists in cache
      if (cache[cacheKey]) {
        setTranslatedText(fixCommonMistranslations(cache[cacheKey]));
        return;
      }

      setIsTranslating(true);
      setError(null);

      try {
        const { data, error: functionError } = await supabase.functions.invoke('translate-hotel-text', {
          body: { text, sourceLang, targetLang }
        });

        if (functionError) {
          throw functionError;
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        if (data?.translatedText) {
          const fixedTranslation = fixCommonMistranslations(data.translatedText);
          setTranslatedText(fixedTranslation);
          
          // Save to cache (with fixes applied)
          cache[cacheKey] = fixedTranslation;
          saveCache(cache);
        } else {
          // Fallback to original text if translation fails
          setTranslatedText(text);
        }
      } catch (err) {
        console.error('Translation error:', err);
        setError(err instanceof Error ? err.message : 'Translation failed');
        // Fallback to original text on error
        setTranslatedText(text);
      } finally {
        setIsTranslating(false);
      }
    };

    translateText();
  }, [text, sourceLang, targetLang]);

  return { translatedText, isTranslating, error };
};
